import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'

import { cn } from '#/shared/lib/utils'
import {
  CallInboundIcon,
  CallMissedIcon,
  CallOutboundIcon,
  ChevronDownIcon,
} from '#/shared/ui/icons'

import type { TimelineItem } from '../../model/conversation'
import { AiTag } from './ai-tag'
import { CallRecordingAudio } from './call-recording-audio'

type CallItem = Extract<TimelineItem, { kind: 'call' }>

/** A declined inbound call shares the missed-call glyph (but stays gray, not red);
 *  otherwise the glyph follows direction. */
function callGlyph(call: CallItem): typeof CallMissedIcon {
  if (call.missed || call.declined) return CallMissedIcon
  return call.direction === 'in' ? CallInboundIcon : CallOutboundIcon
}

/** Chip tone by outcome: missed → red, declined/no-answer → neutral gray,
 *  answered inbound → green, answered outbound → blue (per Figma). */
function chipClass(call: CallItem): string {
  if (call.missed) return 'bg-avatar-red-soft text-avatar-red'
  if (call.declined || call.noAnswer) return 'bg-paper-avatar text-gray-500'
  if (call.direction === 'in') return 'bg-avatar-green-soft text-avatar-green'
  return 'bg-avatar-blue-soft text-avatar-blue'
}

function CallHeader({
  call,
  collapsible,
  expanded,
}: {
  call: CallItem
  collapsible: boolean
  expanded: boolean
}) {
  const missed = call.missed
  const Glyph = callGlyph(call)
  return (
    <div className="flex items-center gap-2.5">
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-full',
          chipClass(call),
        )}
      >
        <Glyph className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 text-sm font-medium text-gray-900">{call.title}</span>
            {call.agent && (
              <>
                <span className="text-warm-500 shrink-0 text-xs">·</span>
                <span className="text-warm-500 min-w-0 truncate text-xs">{call.agent}</span>
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {call.ai && <AiTag />}
            <span className="text-warm-500 text-xs whitespace-nowrap">{call.time}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-1.5">
          <span
            className={cn('min-w-0 truncate text-xs', missed ? 'text-avatar-red' : 'text-gray-500')}
          >
            {call.subtitle}
          </span>
          {collapsible && (
            <ChevronDownIcon
              className={cn(
                'text-warm-500 size-2.5 shrink-0 transition-transform',
                expanded && 'rotate-180',
              )}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/** Order label + AI summary; `divider` draws a rule above when content precedes. */
function CallSummaryBlock({ call, divider }: { call: CallItem; divider: boolean }) {
  const { t } = useTranslation()
  if (!call.summary) return null
  return (
    <>
      {divider && <div className="h-px w-full bg-gray-200" />}
      <span className="text-xs font-semibold text-gray-900">
        {t('phonePanel.conversation.aiCallSummary')}
      </span>
      <p className="text-xs leading-normal text-gray-500">{call.summary.summary}</p>
    </>
  )
}

/** Expanded body: the recording player (resolves availability from the API) and
 *  the AI summary. */
function CallDetails({ call }: { call: CallItem }) {
  const orderLabel = call.summary?.orderLabel
  return (
    <div className="animate-slide-down flex flex-col gap-2.5">
      {call.callId && (
        <CallRecordingAudio callId={call.callId} durationSeconds={call.durationSeconds} />
      )}
      {orderLabel && (
        <span className="truncate text-xs font-semibold text-blue-500">{orderLabel}</span>
      )}
      <CallSummaryBlock call={call} divider={Boolean(call.callId) || Boolean(orderLabel)} />
      <TimelineLink callId={call.callId} />
    </div>
  )
}

/**
 * The way into everything the call itself reported, which the thread does not
 * carry: the legs, why each one left, how the connection held up, the journal.
 *
 * Called the intelligence timeline rather than the timeline because the thread
 * around it is one too — what was said, in order — and a bare "Timeline" here
 * would not say which of the two it opens.
 */
function TimelineLink({ callId }: { callId: string | null | undefined }) {
  if (!callId) return null
  const organizationId = getAdminOrgScope()
  return (
    <Link
      to="/calls/$callId"
      params={{ callId }}
      search={organizationId ? { organizationId } : {}}
      className="text-primary w-fit text-xs hover:underline"
    >
      Intelligence timeline
    </Link>
  )
}

export function CallMessage({
  call,
  highlighted = false,
}: {
  call: CallItem
  /** Flash the card — true when it's the entry jumped to, which for a call means
   *  a search hit on its transcript or voicemail text. */
  highlighted?: boolean
}) {
  const [open, setOpen] = useState(false)
  // Only calls with a recording or an AI summary expand. Missed calls have
  // neither by default, so they render as a static row — no dropdown.
  const expandable = Boolean(call.hasRecording) || Boolean(call.summary)

  // Collapsed (and non-expandable) call events sit flat on the chat with no
  // surface; expanding lifts the row onto a glass card with a hairline border.
  // The bottom tail tightens to 4px on the call's side — inbound left, outbound
  // right — matching the message bubbles.
  const surface = cn(
    'flex w-full flex-col gap-2.5 rounded-2xl p-3',
    call.direction === 'in' ? 'rounded-bl-sm' : 'rounded-br-sm',
    open && 'border border-border bg-chat-incoming/50',
    highlighted && 'animate-highlight-flash',
  )

  // `data-message-id` is what the jump-to-message scroll looks the entry up by
  // (see `useMessageHighlight`) — a call matched on its transcript is a valid
  // search hit, so it needs the anchor as much as a bubble does.
  if (!expandable) {
    return (
      <div data-message-id={call.id} className={surface}>
        <CallHeader call={call} collapsible={false} expanded={false} />
      </div>
    )
  }

  return (
    <div data-message-id={call.id} className={surface}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="cursor-pointer text-left"
      >
        <CallHeader call={call} collapsible expanded={open} />
      </button>

      {open && <CallDetails call={call} />}
    </div>
  )
}
